// Колонки «Рекламы»: что можно показать в таблице, как подписать и отформатировать, как сортировать.
// Цифры считает api; здесь только подписи и формат (DESIGN §3: `$1 234.50`, проценты с одним знаком).
import { int, pct, signedUsd, usd } from '@autoaff/ui';
import { useCallback, useState } from 'react';
import type { AdMetrics, AdsView } from '../../business';
import { compareBy, type SortDir } from '../../filters';

export type ColKey =
  | 'spend'
  | 'impressions'
  | 'ctr'
  | 'linkClicks'
  | 'cpc'
  | 'cpm'
  | 'frequency'
  | 'hookRate'
  | 'leads'
  | 'cpl'
  | 'breakEvenCpl'
  | 'approved'
  | 'approveRate'
  | 'approveCost'
  | 'approveCostForecast'
  | 'inProcess'
  | 'revenue'
  | 'profit'
  | 'forecastProfit';

export interface Column {
  key: ColKey;
  /** Заголовок колонки (коротко). */
  label: string;
  /** Что это — в меню «Колонки» и подсказке заголовка. */
  hint: string;
  value: (m: AdMetrics) => number | null;
  format: (m: AdMetrics) => string;
  /** Цвет по смыслу — только для денег, которые бывают в минусе. */
  signed?: boolean;
  /** Цвет по экономике оффера (CPL против целевого и окупаемого) и подсказка к ячейке. */
  zone?: (m: AdMetrics) => { tone: 'pos' | 'warn' | 'neg'; title: string } | null;
  /** Первый клик по заголовку: цены — сначала дешевле. */
  firstDir: SortDir;
}

const money = (n: number | null | undefined) => (n === null || n === undefined ? '—' : usd(n));
const percent = (n: number | null | undefined) => (n === null || n === undefined ? '—' : pct(n));

/**
 * CPL против экономики оффера: не дороже целевого — зелёный, между целевым и окупаемым — жёлтый (в плюс, но меньше
 * вашей маржи), дороже окупаемого — красный (в минус). Оффер строки неизвестен — без цвета.
 */
export function cplZone(m: AdMetrics): { tone: 'pos' | 'warn' | 'neg'; title: string } | null {
  const be = m.breakEvenCpl ?? null;
  const target = m.targetCpl ?? null;
  if (m.cpl === null || be === null || target === null) return null;
  const title = `Целевой CPL ${usd(target)}, окупаемый ${usd(be)}${m.econThin ? ' (мало данных по офферу)' : ''}`;
  if (m.cpl <= target) return { tone: 'pos', title: `Не дороже целевого. ${title}` };
  if (m.cpl <= be) return { tone: 'warn', title: `В плюс, но меньше вашей маржи. ${title}` };
  return { tone: 'neg', title: `Дороже окупаемого — в минус. ${title}` };
}

export const COLUMNS: Column[] = [
  {
    key: 'spend',
    label: 'Расход',
    hint: 'Расход из Meta',
    value: (m) => m.spend,
    format: (m) => usd(m.spend),
    firstDir: 'desc',
  },
  {
    key: 'impressions',
    label: 'Показы',
    hint: 'Показы объявлений',
    value: (m) => m.impressions,
    format: (m) => int(m.impressions),
    firstDir: 'desc',
  },
  {
    key: 'ctr',
    label: 'CTR',
    hint: 'Доля кликов по ссылке от показов',
    value: (m) => m.ctr,
    format: (m) => percent(m.ctr),
    firstDir: 'desc',
  },
  {
    key: 'linkClicks',
    label: 'Клики',
    hint: 'Клики по ссылке',
    value: (m) => m.linkClicks,
    format: (m) => int(m.linkClicks),
    firstDir: 'desc',
  },
  {
    key: 'cpc',
    label: 'CPC',
    hint: 'Цена клика по ссылке',
    value: (m) => m.cpc,
    format: (m) => money(m.cpc),
    firstDir: 'asc',
  },
  {
    key: 'cpm',
    label: 'CPM',
    hint: 'Цена 1000 показов',
    value: (m) => m.cpm,
    format: (m) => money(m.cpm),
    firstDir: 'asc',
  },
  {
    key: 'frequency',
    label: 'Частота',
    hint: 'Сколько раз в среднем человек видел объявление: показы / охват (за период длиннее дня — примерно)',
    value: (m) => m.frequency ?? null,
    format: (m) => (m.frequency === null || m.frequency === undefined ? '—' : m.frequency.toFixed(2)),
    firstDir: 'desc',
  },
  {
    key: 'hookRate',
    label: 'Досмотр 3 с',
    hint: 'Доля показов, где ролик смотрели 3 секунды и дольше: цепляет ли начало',
    value: (m) => m.hookRate ?? null,
    format: (m) => percent(m.hookRate),
    firstDir: 'desc',
  },
  {
    key: 'leads',
    label: 'Лиды',
    hint: 'Лиды платформы',
    value: (m) => m.leads,
    format: (m) => int(m.leads),
    firstDir: 'desc',
  },
  {
    key: 'cpl',
    label: 'CPL',
    hint: 'Цена лида; цвет — против целевого и окупаемого CPL оффера',
    value: (m) => m.cpl,
    format: (m) => money(m.cpl),
    zone: cplZone,
    firstDir: 'asc',
  },
  {
    key: 'breakEvenCpl',
    label: 'Окупаемый CPL',
    hint: 'Сколько можно платить за лид в ноль: прогнозный апрув оффера × выплата. Есть у строк, где оффер один',
    value: (m) => m.breakEvenCpl ?? null,
    format: (m) => money(m.breakEvenCpl),
    firstDir: 'desc',
  },
  {
    key: 'approved',
    label: 'Апрувы',
    hint: 'Апрувы',
    value: (m) => m.approved,
    format: (m) => int(m.approved),
    firstDir: 'desc',
  },
  {
    key: 'approveRate',
    label: '% апрува',
    hint: 'Апрув от решённых: апрувы / (апрувы + отказы + мусор + дубли); лиды в обработке не входят',
    value: (m) => m.approveRate,
    format: (m) => percent(m.approveRate),
    firstDir: 'desc',
  },
  {
    key: 'approveCost',
    label: 'Цена апрува',
    hint: 'Расход на один апрув',
    value: (m) => m.approveCost,
    format: (m) => money(m.approveCost),
    firstDir: 'asc',
  },
  {
    key: 'approveCostForecast',
    label: 'Цена апрува, прогноз',
    hint: 'Расход / (апрувы + ожидаемые апрувы лидов в обработке по офферу и возрасту лида)',
    value: (m) => m.approveCostForecast ?? null,
    format: (m) => money(m.approveCostForecast),
    firstDir: 'asc',
  },
  {
    key: 'inProcess',
    label: 'В обработке',
    hint: 'Лиды без решения: в обработке, холд, перезвон',
    value: (m) => m.inProcess,
    format: (m) => int(m.inProcess),
    firstDir: 'desc',
  },
  {
    key: 'revenue',
    label: 'Выплаты',
    hint: 'Выплаты за апрувы',
    value: (m) => m.revenue,
    format: (m) => usd(m.revenue),
    firstDir: 'desc',
  },
  {
    key: 'profit',
    label: 'Прибыль',
    hint: 'Выплаты минус расход',
    value: (m) => m.profit,
    format: (m) => signedUsd(m.profit),
    signed: true,
    firstDir: 'desc',
  },
  {
    key: 'forecastProfit',
    label: 'Прогноз',
    hint: 'Прибыль с лидами в обработке: вероятность апрува по офферу и возрасту лида × выплата',
    value: (m) => m.forecastProfit,
    format: (m) => signedUsd(m.forecastProfit),
    signed: true,
    firstDir: 'desc',
  },
];

export const COLUMN = Object.fromEntries(COLUMNS.map((c) => [c.key, c])) as Record<ColKey, Column>;
export const DEFAULT_COLS: ColKey[] = [
  'spend',
  'leads',
  'cpl',
  'approved',
  'approveRate',
  'approveCost',
  'profit',
  'forecastProfit',
];

/**
 * Колонки по умолчанию: на широком экране (от 1400 px) — ещё окупаемый CPL рядом с CPL; в 1280 он не помещается
 * без прокрутки, там окупаемый и целевой видны цветом CPL и подсказкой, а колонка — в «Колонках».
 */
export function defaultCols(): ColKey[] {
  try {
    if (window.matchMedia('(min-width: 1400px)').matches)
      return DEFAULT_COLS.flatMap((k) => (k === 'cpl' ? ['cpl', 'breakEvenCpl'] : [k])) as ColKey[];
  } catch {
    /* нет matchMedia — как на узком */
  }
  return DEFAULT_COLS;
}

/** Цвет денег по знаку. */
export const tone = (n: number) => (n > 0 ? 'pos' : n < 0 ? 'neg' : undefined);

// ─── сортировка ────────────────────────────────────────────────────────
/** name — по названию (в разрезе «Дни» — по дате). */
export type SortKey = ColKey | 'name';
export interface Sort {
  key: SortKey;
  dir: SortDir;
}
const SORT_KEYS = new Set<string>([...COLUMNS.map((c) => c.key), 'name']);
/** «spend:desc» → сортировка; непонятное — fallback. */
export const sortOf = (v: string | undefined, fallback: Sort): Sort => {
  const [key = '', dir = ''] = (v ?? '').split(':');
  return SORT_KEYS.has(key) && (dir === 'asc' || dir === 'desc') ? { key: key as SortKey, dir } : fallback;
};
export const sortValue = (s: Sort) => `${s.key}:${s.dir}`;

/** По умолчанию — больше расход; дни — сначала новые. */
const DEFAULT_SORT: Record<AdsView, Sort> = {
  campaigns: { key: 'spend', dir: 'desc' },
  ads: { key: 'spend', dir: 'desc' },
  offers: { key: 'spend', dir: 'desc' },
  creatives: { key: 'spend', dir: 'desc' },
  days: { key: 'name', dir: 'desc' },
};
const SORT_STORE = 'ads:sort';

function readSorts(): Record<AdsView, Sort> {
  try {
    const raw = JSON.parse(localStorage.getItem(SORT_STORE) ?? '{}') as Record<string, string>;
    return Object.fromEntries(
      Object.entries(DEFAULT_SORT).map(([view, def]) => [view, sortOf(raw[view], def)]),
    ) as Record<AdsView, Sort>;
  } catch {
    return DEFAULT_SORT;
  }
}

/**
 * Сортировка таблицы — своя у каждого разреза, браузер её помнит. toggle — клик по заголовку: повторный клик
 * по той же колонке переворачивает порядок, новая колонка — сначала в её естественную сторону.
 */
export function useViewSort(view: AdsView): {
  sort: Sort;
  toggle: (key: SortKey, firstDir?: SortDir) => void;
  set: (s: Sort) => void;
} {
  const [all, setAll] = useState(readSorts);
  const sort = all[view];
  const set = useCallback(
    (s: Sort) =>
      setAll((cur) => {
        const next = { ...cur, [view]: s };
        try {
          localStorage.setItem(
            SORT_STORE,
            JSON.stringify(Object.fromEntries(Object.entries(next).map(([k, v]) => [k, sortValue(v)]))),
          );
        } catch {
          /* приватный режим — просто не помним */
        }
        return next;
      }),
    [view],
  );
  const toggle = useCallback(
    (key: SortKey, firstDir: SortDir = 'desc') =>
      set(sort.key === key ? { key, dir: sort.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: firstDir }),
    [set, sort],
  );
  return { sort, toggle, set };
}

/**
 * Строки по выбранной сортировке. Пустые значения (нет лидов — нет CPL) — в конце; строки-«корзины»
 * («Без оффера», «Без ролика из пула», id = 'none') — всегда последними.
 */
export function sortRows<T extends AdMetrics & { name: string; id?: string }>(rows: T[], s: Sort): T[] {
  const k = s.key;
  const cmp = compareBy<T>(k === 'name' ? (r) => r.name : (r) => COLUMN[k].value(r), s.dir);
  return [...rows].sort((a, b) => Number(a.id === 'none') - Number(b.id === 'none') || cmp(a, b));
}

/** На телефоне в строке три цифры — расход, лиды, прибыль — и та, по которой сортируют. */
export function phoneCols(sort: Sort): ColKey[] {
  const base: ColKey[] = ['spend', 'leads', 'profit'];
  return sort.key === 'name' || base.includes(sort.key) ? base : [...base.slice(0, 2), sort.key, 'profit'];
}

/** Сортировка на телефоне (шапки таблицы там нет) — словами, а не стрелками. */
const PHONE_SORTS: Array<{ value: string; label: string }> = [
  { value: 'spend:desc', label: 'Больше расход' },
  { value: 'leads:desc', label: 'Больше лидов' },
  { value: 'cpl:asc', label: 'Дешевле лид' },
  { value: 'approved:desc', label: 'Больше апрувов' },
  { value: 'approveRate:desc', label: 'Выше % апрува' },
  { value: 'approveCost:asc', label: 'Дешевле апрув' },
  { value: 'profit:desc', label: 'Больше прибыль' },
  { value: 'profit:asc', label: 'Сначала убыточные' },
  { value: 'forecastProfit:desc', label: 'Лучше прогноз' },
];

/** Пункты «Сортировки» на телефоне для разреза; выбранная на компьютере колонка — тоже в списке. */
export function phoneSorts(view: AdsView, current: Sort): Array<{ value: string; label: string }> {
  const list = [
    ...(view === 'days'
      ? [
          { value: 'name:desc', label: 'Сначала новые дни' },
          { value: 'name:asc', label: 'Сначала старые дни' },
        ]
      : []),
    ...PHONE_SORTS,
    ...(view === 'days' ? [] : [{ value: 'name:asc', label: 'По названию' }]),
  ];
  const v = sortValue(current);
  if (!list.some((o) => o.value === v) && current.key !== 'name')
    list.push({
      value: v,
      label: `${COLUMN[current.key].label}: ${current.dir === 'desc' ? 'больше' : 'меньше'}`,
    });
  return list;
}

// ─── память выбора колонок ─────────────────────────────────────────────
const COLS_KEY = 'ads:columns';

function readCols(): ColKey[] {
  try {
    const s = localStorage.getItem(COLS_KEY);
    if (!s) return defaultCols();
    const keys = s.split(',').filter((k): k is ColKey => k in COLUMN);
    return keys.includes('spend') ? keys : ['spend', ...keys];
  } catch {
    return defaultCols();
  }
}

/** Выбранные колонки таблицы: порядок — как в COLUMNS, «Расход» всегда есть. Выбор помнит браузер. */
export function useColumns(): [ColKey[], (cols: ColKey[]) => void] {
  const [cols, setCols] = useState<ColKey[]>(readCols);
  const set = useCallback((next: ColKey[]) => {
    const ordered = COLUMNS.map((c) => c.key).filter((k) => k === 'spend' || next.includes(k));
    setCols(ordered);
    try {
      localStorage.setItem(COLS_KEY, ordered.join(','));
    } catch {
      /* приватный режим — просто не помним */
    }
  }, []);
  return [cols, set];
}
